import { useCallback, useEffect, useRef, useState } from 'react';

export interface PagedListPage<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface PagedListParams {
  page: number;
  pageSize: number;
  search: string;
}

export interface UsePagedListOptions<T> {
  fetcher: (params: PagedListParams) => Promise<PagedListPage<T>>;
  initialPageSize?: number;
  /** Seed the search box on mount (e.g., from a URL search param). */
  initialSearch?: string;
  searchDebounceMs?: number;
  onError?: (error: unknown) => void;
  /**
   * Fires after the search is debounced — same trigger as a refetch. Use this
   * to mirror the search term into a URL param without spamming history on
   * every keystroke.
   */
  onSearchChange?: (search: string) => void;
  /**
   * Additional values (filters, etc.) that should trigger a refetch when they
   * change. The page resets to 1 whenever any of these change. Compared by
   * shallow JSON serialization, so prefer primitive values.
   */
  deps?: ReadonlyArray<unknown>;
}

export interface UsePagedListResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  search: string;
  loading: boolean;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSearch: (term: string) => void;
  refresh: () => void;
}

export function usePagedList<T>({
  fetcher,
  initialPageSize = 25,
  initialSearch = '',
  searchDebounceMs = 300,
  onError,
  onSearchChange,
  deps,
}: UsePagedListOptions<T>): UsePagedListResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [search, setSearchState] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [loading, setLoading] = useState(false);

  // Keep latest callbacks/values in refs so the load effect only depends on
  // the actual paging triggers (page/pageSize/debouncedSearch).
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;

  const reqIdRef = useRef(0);

  // Reduce caller-provided `deps` to a stable string we can include as an
  // effect dep. Identity comparison would refire constantly when callers pass
  // inline arrays/objects.
  const depsKey = JSON.stringify(deps ?? []);

  useEffect(() => {
    if (search === debouncedSearch) return;
    const timer = setTimeout(() => setDebouncedSearch(search), searchDebounceMs);
    return () => clearTimeout(timer);
  }, [search, debouncedSearch, searchDebounceMs]);

  // When debounced search or caller-supplied deps change, jump back to page 1.
  // Also notify the caller (used to mirror the term into URL params).
  const prevSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    if (prevSearchRef.current !== debouncedSearch) {
      prevSearchRef.current = debouncedSearch;
      setPageState(1);
      onSearchChangeRef.current?.(debouncedSearch);
    }
  }, [debouncedSearch]);

  const prevDepsKeyRef = useRef(depsKey);
  useEffect(() => {
    if (prevDepsKeyRef.current !== depsKey) {
      prevDepsKeyRef.current = depsKey;
      setPageState(1);
    }
  }, [depsKey]);

  const load = useCallback(async () => {
    const id = ++reqIdRef.current;
    setLoading(true);
    try {
      const result = await fetcherRef.current({ page, pageSize, search: debouncedSearch });
      if (id !== reqIdRef.current) return;
      setItems(result.items ?? []);
      setTotalCount(result.totalCount ?? 0);
    } catch (err) {
      if (id !== reqIdRef.current) return;
      setItems([]);
      setTotalCount(0);
      onErrorRef.current?.(err);
    } finally {
      if (id === reqIdRef.current) setLoading(false);
    }
  }, [page, pageSize, debouncedSearch]);

  useEffect(() => {
    load();
    // depsKey participates so a filter change re-fetches even when page was
    // already 1 (load identity wouldn't change in that case).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, depsKey]);

  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    setPageState(1);
  }, []);

  return {
    items,
    totalCount,
    page,
    pageSize,
    search,
    loading,
    setPage: setPageState,
    setPageSize,
    setSearch: setSearchState,
    refresh: load,
  };
}
